import { supabase } from './supabase';
import type { ClientBilling } from '../store/types';

/**
 * Stripe Connect client wrappers (web). The coach links their own Stripe
 * account (Standard, hosted onboarding) and bills clients with direct charges;
 * all card handling is Stripe-hosted. These thin wrappers call the edge
 * functions — secrets live only server-side. Mirrors apps/mobile/src/services/billing.ts.
 */

/**
 * Ask stripe-connect-start for a fresh hosted-onboarding link. The caller opens
 * it (new tab on web); when the coach finishes, the account.updated webhook
 * writes stripe_connect_status — refreshSelf() picks it up on return.
 */
export const startStripeConnect = async (): Promise<{ ok: true; url: string } | { ok: false; error: string }> => {
  const { data, error } = await supabase.functions.invoke('stripe-connect-start', { body: {} });
  if (error) return { ok: false, error: error.message };
  if (!data?.ok || !data?.url) return { ok: false, error: data?.error ?? 'Could not start Stripe onboarding.' };
  return { ok: true, url: data.url as string };
};

/** Create a hosted Checkout link to bill a client. Returns the URL to open/share. */
export const billClient = async (clientId: string): Promise<{ ok: true; url: string } | { ok: false; error: string }> => {
  const { data, error } = await supabase.functions.invoke('stripe-billing', { body: { clientId } });
  if (error) {
    // Edge functions return their real message in the response body on non-2xx.
    let msg = error.message;
    try { msg = (await (error as { context?: { json: () => Promise<{ error?: string }> } }).context!.json()).error ?? msg; } catch { /* keep error.message */ }
    return { ok: false, error: msg };
  }
  if (!data?.ok || !data?.url) return { ok: false, error: data?.error ?? 'Could not start checkout.' };
  return { ok: true, url: data.url as string };
};

export interface BillingDisplay {
  text: string;
  tone: 'good' | 'warn' | 'bad' | 'muted';
}

/**
 * Human-readable billing status for a client profile, derived from the synced
 * client_billing cache. Maps Stripe subscription/invoice statuses (plus our own
 * 'checkout_created') to a short label + a tone the UI colors. null => not set up.
 */
export const describeBilling = (b: ClientBilling | null): BillingDisplay => {
  if (!b || !b.status) return { text: 'No billing set up yet', tone: 'muted' };
  const amount = b.amountCents != null ? `$${(b.amountCents / 100).toFixed(0)}` : '';
  const money = amount && b.billingInterval ? `${amount}/${b.billingInterval}` : amount;
  const suffix = money ? ` · ${money}` : '';
  switch (b.status) {
    case 'active':
    case 'trialing':
    case 'paid': {
      const renews = b.currentPeriodEnd ? ` · renews ${new Date(b.currentPeriodEnd).toLocaleDateString()}` : '';
      return { text: `Active${suffix}${renews}`, tone: 'good' };
    }
    case 'past_due':
    case 'unpaid':
      return { text: `Past due${suffix}`, tone: 'bad' };
    case 'checkout_created':
      return { text: `Awaiting first payment${suffix}`, tone: 'warn' };
    case 'canceled':
    case 'incomplete_expired':
      return { text: 'Canceled', tone: 'muted' };
    default:
      return { text: `${b.status}${suffix}`, tone: 'warn' };
  }
};

/** Compact one-word badge for roster rows. null => no billing, render nothing. */
export const billingBadge = (b: ClientBilling | null): { label: string; tone: BillingDisplay['tone'] } | null => {
  if (!b || !b.status) return null;
  switch (b.status) {
    case 'active':
    case 'trialing':
    case 'paid':
      return { label: 'Active', tone: 'good' };
    case 'past_due':
    case 'unpaid':
      return { label: 'Past due', tone: 'bad' };
    case 'checkout_created':
      return { label: 'Awaiting', tone: 'warn' };
    case 'canceled':
    case 'incomplete_expired':
      return { label: 'Canceled', tone: 'muted' };
    default:
      return { label: b.status, tone: 'warn' };
  }
};

/** CSS color for a billing tone — the app's restrained status palette. */
export const billingToneColor = (tone: BillingDisplay['tone']): string =>
  tone === 'good' ? 'var(--status-green)'
  : tone === 'warn' ? 'var(--status-yellow)'
  : tone === 'bad' ? 'var(--status-red)'
  : 'var(--text-muted)';
